/**
 * RankedContractRow: one row of the ranked contracts table -- tick, size, lot-minimum reason, Desk state, Priority
 * chip and the analytics cells. Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28).
 * It queues nothing itself: ticking and typing update ScreenView state; the ticket path stays in ScreenView.
 */
import React from "react";
import { badgeStyle, mutedText, ui } from "@components/screener/screenerUi";
import { currency, deskCell, numberFmt, pctFmt, roundTripSpreadPct, rowReasonToShow, titleizeBucket, titleizeCode, vegaPer1k } from "@lib/screenerTruth";
import { isRankedCandidate, titleizeSkipReason, toneFromEntryGrade, toneFromShapeLabel, toneFromSpread, toneFromTenorBucket, toneFromValueLabel } from "@lib/screenerViewHelpers";
import { HelionButton } from "@components/ui/HelionButton";
import { RungStatusChip } from "@components/screener/LadderPriorityPanel";
import { type Purchase, type ScreenerResponse, type TicketState } from "@lib/screenerTypes";
import { type LotMinimumRule, type ScreenerLotPreview } from "@lib/lotMinimum";

export type RankedContractRowProps = {
  actionBusy: boolean;
  cioQtyByConid: Record<string, string>;
  contractKey: string;
  data: ScreenerResponse;
  decisionReason: string;
  i: number;
  isExpanded: boolean;
  lotExceptionByConid: Record<string, string>;
  lotPreviewFor: (p: Purchase) => ScreenerLotPreview;
  lotRule: LotMinimumRule | null;
  marketRankOnly: boolean;
  needsCioQty: boolean;
  p: Purchase;
  resolveTicketSizing: (p: Purchase) => { qty: number; notional: number | null; origin: "SCREENER" | "CIO"; cioSized: boolean; };
  rowBlocker: string | null;
  rowKey: string;
  rungByExpiry: Map<string, { rollingOff?: boolean | null | undefined; status?: string | null | undefined; }>;
  selected: boolean;
  selectedForQueue: Set<string>;
  setCioQtyByConid: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setExpandedContractKey: React.Dispatch<React.SetStateAction<string | null>>;
  setLotExceptionByConid: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setSelectedForQueue: React.Dispatch<React.SetStateAction<Set<string>>>;
  showAnalyticsColumns: boolean;
  stageSinglePurchase: (p: Purchase) => Promise<void>;
  stagingAll: boolean;
  tickable: boolean;
  ticketState: TicketState | undefined;
  trustHardBlock: boolean;
};

export default function RankedContractRow({
  actionBusy,
  cioQtyByConid,
  contractKey,
  data,
  decisionReason,
  i,
  isExpanded,
  lotExceptionByConid,
  lotPreviewFor,
  lotRule,
  marketRankOnly,
  needsCioQty,
  p,
  resolveTicketSizing,
  rowBlocker,
  rowKey,
  rungByExpiry,
  selected,
  selectedForQueue,
  setCioQtyByConid,
  setExpandedContractKey,
  setLotExceptionByConid,
  setSelectedForQueue,
  showAnalyticsColumns,
  stageSinglePurchase,
  stagingAll,
  tickable,
  ticketState,
  trustHardBlock,
}: RankedContractRowProps) {
  return (
    <tr
      key={`${p.conid}-${i}`}
      style={{
        backgroundColor: i % 2 ? "transparent" : ui.rowAlt,
        borderTop: `1px solid ${ui.cardBorder}`,
      }}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          aria-label={
            tickable
              ? `Select ${p.expiry} ${p.right}${numberFmt(p.strike, 0)} to queue`
              : "Already queued"
          }
          title={
            ticketState
              ? "Already queued"
              : needsCioQty
              ? "Tick to buy this — the allocator did not size it, so enter a quantity in the Qty column"
              : "Tick to queue this contract to the Trading Desk"
          }
          disabled={!tickable || trustHardBlock || stagingAll}
          checked={selectedForQueue.has(rowKey)}
          onChange={(e) =>
            setSelectedForQueue((prev) => {
              const next = new Set(prev);
              if (e.target.checked) next.add(rowKey);
              else next.delete(rowKey);
              return next;
            })
          }
        />
        {selectedForQueue.has(rowKey) && rowBlocker ? (
          <div className="mt-1 text-[10px]" style={{ color: "rgb(var(--c-warn))" }}>
            {rowBlocker}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 font-semibold" style={{ whiteSpace: "nowrap" }}>{p.expiry}</td>
      <td className="px-3 py-2 font-semibold">{numberFmt(p.strike, 2)}</td>
      <td className="px-3 py-2">{numberFmt(p.mid ?? p.midPrice ?? null, 2)}</td>
      {(() => {
        const spread = roundTripSpreadPct(p);
        return (
          <td
            className="px-3 py-2"
            title={
              spread == null
                ? "No two-sided quote, so the round-trip cost is unknown. It is NOT zero — verify in TWS before sizing. A missing or crossed book is scored neutral in quote quality, never as tight."
                : `Round-trip quoted cost = (ask ${numberFmt(p.ask, 2)} - bid ${numberFmt(p.bid, 2)}) / mid ${numberFmt(p.mid ?? p.midPrice ?? null, 2)}. This is what buying and later selling at the quoted market costs you before any market move. It is NOT charged in the entry score or the grade.`
            }
          >
            <span style={badgeStyle(toneFromSpread(spread))}>
              {spread == null ? "—" : pctFmt(spread, 1)}
            </span>
          </td>
        );
      })()}
      <td className="px-3 py-2">
        {needsCioQty && selectedForQueue.has(rowKey) ? (
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            aria-label={`Quantity for ${p.expiry} ${p.right}${numberFmt(p.strike, 0)}`}
            placeholder="qty"
            value={cioQtyByConid[rowKey] ?? ""}
            disabled={stagingAll || trustHardBlock}
            onChange={(e) =>
              setCioQtyByConid((prev) => ({ ...prev, [rowKey]: e.target.value }))
            }
            className="w-20 rounded px-2 py-1 text-sm"
            style={{
              backgroundColor: ui.inputBg,
              border: `1px solid ${ui.inputBorder}`,
              color: ui.textPrimary,
            }}
          />
        ) : (
          // Not-computed, not zero: the allocator does not run in
          // market-rank-only mode. The CIO's own quantity input above is
          // untouched.
          marketRankOnly ? "—" : (p.allocatedQty || 0)
        )}
        {needsCioQty && selectedForQueue.has(rowKey) && Number(p.mid) > 0 ? (
          <div className="mt-1 text-[10px]" style={mutedText}>
            {(() => {
              const s = resolveTicketSizing(p);
              return s.notional != null ? `≈ ${currency(s.notional)}` : "enter qty";
            })()}
          </div>
        ) : null}
        {(() => {
          // Trimmable-lot minimum (2026-09-17). "held N" on every row the book
          // holds, so a +1 that fixes a one-lot reads as a top-up; the reason box
          // only on a ticked row that would open a new line below the minimum.
          const heldQty = lotRule?.heldBookReadable ? lotRule.heldByContract[rowKey] ?? 0 : null;
          const ticked = selectedForQueue.has(rowKey);
          // No quantity yet -> no verdict; "enter a quantity" comes first.
          const lot = ticked && !ticketState && resolveTicketSizing(p).qty > 0 ? lotPreviewFor(p) : null;
          return (
            <>
              {heldQty != null && heldQty > 0 ? (
                <div
                  className="mt-1 text-[10px]"
                  style={mutedText}
                  title="Contracts already held on this line, from the held book (all sub-accounts)."
                >
                  held {heldQty}
                </div>
              ) : null}
              {lot && lot.status === "top_up" ? (
                <div className="mt-1 max-w-[200px] text-[10px] leading-4" style={mutedText}>
                  {lot.message}
                </div>
              ) : null}
              {lot && lot.requiresException ? (
                <div className="mt-1 max-w-[220px]">
                  <div className="text-[10px] leading-4" style={{ color: "rgb(var(--c-warn))" }}>
                    {lot.message}
                  </div>
                  <input
                    type="text"
                    aria-label={`Exception reason for ${p.expiry} ${p.right}${numberFmt(p.strike, 0)}`}
                    placeholder="Exception reason (required to queue)"
                    value={lotExceptionByConid[rowKey] ?? ""}
                    maxLength={500}
                    disabled={stagingAll || trustHardBlock}
                    onChange={(e) =>
                      setLotExceptionByConid((prev) => ({ ...prev, [rowKey]: e.target.value }))
                    }
                    className="mt-1 w-full rounded px-2 py-1 text-xs"
                    style={{
                      backgroundColor: ui.inputBg,
                      border: "1px solid rgb(var(--c-warn) / 0.6)",
                      color: ui.textPrimary,
                    }}
                  />
                </div>
              ) : null}
            </>
          );
        })()}
      </td>
      <td className="px-3 py-2" data-testid="screener-desk-cell">
        {ticketState ? (
          // Phase 1 (finding 3): the ticket state FIRST. The old order tested
          // marketRankOnly first, which is always true, so "Queued" never rendered.
          <span
            style={badgeStyle(
              ticketState.status === "staged"
                ? "success"
                : ticketState.status === "queued"
                ? "info"
                : "warning"
            )}
            title={ticketState.label || ticketState.status}
            data-testid="screener-desk-queued"
          >
            {deskCell(ticketState).text}
          </span>
        ) : marketRankOnly || isRankedCandidate(p) ? (
          <span style={badgeStyle("info")}>Ranked</span>
        ) : !selected ? (
          <span style={badgeStyle("neutral")}>Skipped</span>
        ) : (
          <HelionButton
            onClick={() => stageSinglePurchase(p)}
            disabled={actionBusy || trustHardBlock}
            className="px-3 py-1 text-xs"
          >
            {actionBusy ? "Queueing…" : trustHardBlock ? "Locked" : "Queue BUY"}
          </HelionButton>
        )}
      </td>
      <td className="px-3 py-2" style={{ minWidth: 220 }}>
        <div className="font-medium" style={{ color: ui.textPrimary }}>
          {isRankedCandidate(p)
            ? typeof p.priorityRank === "number"
              ? `Priority #${p.priorityRank} · Score #${p.screenRank || p.rank || i + 1}`
              : `Rank #${p.screenRank || p.rank || i + 1}`
            : selected ? "Selected allocation" : titleizeSkipReason(p.skipReason)}
          {isRankedCandidate(p) && data?.ladderPriority?.active ? (
            <RungStatusChip status={p.rungStatus} rollingOff={rungByExpiry.get(String(p.expiry))?.rollingOff ?? false} />
          ) : null}
        </div>
        {rowReasonToShow(decisionReason) ? (
          <div className="mt-1 text-xs" style={mutedText} data-testid="screener-row-reason">
            {rowReasonToShow(decisionReason)}
          </div>
        ) : null}
      </td>
      {(() => {
        const vpk = vegaPer1k(p);
        return (
          <td
            className="px-3 py-2"
            title={
              vpk == null
                ? "Vega or mid unavailable for this contract."
                : `Dollars of vega per $1,000 of premium = vega ${numberFmt(p.vega, 3)} / mid ${numberFmt(p.mid ?? p.midPrice ?? null, 2)} x 1000. Convexity actually bought per dollar spent; the contract multiplier cancels. Higher is more convexity per dollar.`
            }
          >
            {vpk == null ? "—" : numberFmt(vpk, 1)}
          </td>
        );
      })()}
      <td className="px-3 py-2">
        <span style={badgeStyle(toneFromEntryGrade(p.entryGrade))}>
          {p.entryGrade || "—"}
        </span>
      </td>
      <td className="px-3 py-2">{numberFmt(p.entryScore, 3)}</td>
      <td className="px-3 py-2">{numberFmt(p.surfaceScore, 3)}</td>
      <td className="px-3 py-2">
        <span style={badgeStyle(toneFromValueLabel(p.surfaceValueLabel))}>
          {p.surfaceValueLabel || "—"}
        </span>
      </td>
      <td className="px-3 py-2">
        <span style={badgeStyle(toneFromShapeLabel(p.surfaceShapeLabel))}>
          {p.surfaceShapeLabel || "—"}
        </span>
      </td>
      {/* "Regime" cell removed 2026-08-11 — see the header array. */}
      <td className="px-3 py-2">
        <span style={badgeStyle(toneFromTenorBucket(p.tenorBucket))}>
          {titleizeBucket(p.tenorBucket)}
        </span>
      </td>
      <td className="px-3 py-2">{p.dte}</td>
      {/* OI in the STANDARD view 2026-09-10 (CIO). Tooltip keeps the
          sizing-cap detail that used to live only in the analytics
          view. "Verify" means the API returned no OI — it is NOT a
          zero, and must not be read as one. */}
      <td
        className="px-3 py-2"
        title={
          p.sizingNote ||
          (p.oiCapQty != null
            ? `OI cap ${numberFmt(p.oiCapQty, 0)} · left ${numberFmt(p.oiCapLeft, 0)}`
            : undefined)
        }
      >
        {p.openInterestStatus === "unavailable_verify" ? "Verify" : numberFmt(p.openInterest, 0)}
      </td>
      {showAnalyticsColumns && (
        <>
          {/* "Right" cell removed 2026-08-11 — see the header array. */}
          <td className="px-3 py-2">{pctFmt(p.iv, 1)}</td>
          <td className="px-3 py-2">{pctFmt(p.moneyness, 1)}</td>
          <td className="px-3 py-2">{String(p.conid)}</td>
          <td className="px-3 py-2">{numberFmt(p.bid, 2)}</td>
          <td className="px-3 py-2">{numberFmt(p.ask, 2)}</td>
          {/* SIZING-CAP DETAIL relocated into these three tooltips
              2026-08-11, from the drawer's "Price & allocation
              context" panel. multiplier / sizingNote / oiCapQty /
              oiCapLeft / sizingOiCapPct exist nowhere else on the
              page; they are position-size caps, so they stay. */}
          <td
            className="px-3 py-2"
            title={`Multiplier ${numberFmt(p.multiplier, 0)}`}
          >
            {currency(p.unitCost ?? ((p.mid ?? p.midPrice ?? 0) * (p.multiplier || 100)))}
          </td>
          <td className="px-3 py-2">{numberFmt(p.vega, 3)}</td>
          <td
            className="px-3 py-2"
            title={`Max initial ${numberFmt(p.sizingMaxInitialQty ?? p.oiCapQty ?? null, 0)} · OI cap ${pctFmt(p.sizingOiCapPct, 1)}`}
          >
            <span style={badgeStyle(p.liquidityTier === "VERIFY_OI" ? "warning" : p.liquidityTier === "STARTER_50_99" ? "info" : "success")}>{titleizeCode(p.liquidityTier)}</span>
          </td>
          <td
            className="px-3 py-2"
            title="OI tier and spread blended 0.35 / 0.65. Ranking effect is OFF while quote_quality.mode is 'observe'."
          >
            {numberFmt(p.quoteQualityScore, 3)}
          </td>
          <td className="px-3 py-2" title="Quoted bid size — display only, never scored.">{numberFmt(p.bidSize, 0)}</td>
          <td className="px-3 py-2" title="Quoted ask size — display only, never scored.">{numberFmt(p.askSize, 0)}</td>
          {/* Step 3 (CIO 2026-09-10). When liquidity_policy is on,
              the SHARE-OF-LINE cap is the number shown, because the
              allocator does not run in production and this column is
              what the CIO actually sizes from. The tiered ladder it
              replaces is kept visible underneath so the observe
              window is a like-for-like comparison. */}
          <td
            className="px-3 py-2"
            title={
              p.liquidityPolicyMode && p.liquidityPolicyMode !== "off"
                ? `Share-of-line cap ${pctFmt(p.shareOfLineCapPct, 0)} of this line's OI. Tiered ladder would allow ${numberFmt(p.tieredCapQty, 0)}. Policy mode: ${p.liquidityPolicyMode} — in "observe" no quantity is changed.`
                : `Tiered OI cap ${pctFmt(p.sizingOiCapPct, 1)}`
            }
          >
            {p.liquidityPolicyMode && p.liquidityPolicyMode !== "off" ? (
              <>
                {numberFmt(p.shareOfLineCapQty, 0)}
                <div className="text-[10px]" style={mutedText}>
                  tiered {numberFmt(p.tieredCapQty, 0)}
                </div>
              </>
            ) : (
              numberFmt(p.sizingMaxInitialQty ?? p.oiCapQty ?? null, 0)
            )}
          </td>
          <td className="px-3 py-2">
            <HelionButton
              type="button"
              variant="secondary"
              className="px-3 py-1 text-xs"
              onClick={() =>
                setExpandedContractKey((prev) =>
                  prev === contractKey ? null : contractKey
                )
              }
            >
              {isExpanded ? "Hide" : "Open"}
            </HelionButton>
          </td>
        </>
      )}
    </tr>
  );
}
